import React, { useEffect, useState } from 'react';

import _ from 'lodash';

import ExamViewer from '../../../components/ExamViewer';
import {
  AttemptWithMark,
  ExamAttempt,
  Section,
} from '../../../components/ExamViewer/types';
import Modal from '../../../components/Modal';
import { NoPermissionMessage } from '../../../components/PageLayout';
import SkeletonStack from '../../../components/SkeletonStack';
import {
  useGetExamAttemptQuery, useGetExamQuery, useMarkQuestionMutation,
} from '../../../services/exams';
import { Question } from '../../../services/exams/types';
import { useGetStudentQuery } from '../../../services/students';
import { QuestionType } from '../../../types/common';
import { formatQuestionLabel, getQuestionSections } from '../../../utils/common/exams';
import useSnackBar from '../../../utils/react/hooks/useSnackbar';
import ResultsDialog from '../ResultsDialog';
import {
  getExamMCQMarks,
  getIsAttemptFinalised,
  questionAttemptsArrayToExamAttempt,
} from '../utils';

interface MarkingPageProps {
  subject: string,
  studentId: string,
  examId: string,
  attemptNum: number
}

const MarkingPage = ({ subject, studentId, examId, attemptNum }: MarkingPageProps) => {
  const [loadExamAttempt, setLoadExamAttempt] = useState(true);

  const { data: exam, isSuccess: isExamLoaded, isLoading: isExamLoading } =
    useGetExamQuery({ subject, examId });
  const {
    data: examAttempt,
    isLoading: isExamAttemptLoading,
    isSuccess: isExamAttemptLoaded,
    isError: isExamAttemptError,
  } = useGetExamAttemptQuery({
    subject, examId, studentId, attemptNum,
  }, { skip: !loadExamAttempt });

  const { data: student, isLoading: isStudentLoading } = useGetStudentQuery({ studentId });

  const [isMarked, setIsMarked] = useState(false);
  const [isMarkingLoading, setIsMarkingLoading] = useState(false);
  const [attempts, setAttempts] = useState<ExamAttempt>({});
  const [questionSectionIndex, setQuestionSectionIndex] = useState(0);
  const [questionSections, setQuestionSections]
   = useState<Section[]>([]);
  const [isMCQ, setIsMCQ] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isUnrecoverableError, setIsUnrecoverableError] = useState(false);

  const [putMark,
    {
      isLoading: isPutMarkLoading,
      isError: isPutMarkError,
      isSuccess: isPutMarkSuccess,
    }] = useMarkQuestionMutation();

  const [displayedQuestions, setDisplayedQuestions] = useState<Question[]>([]);

  const [MCQMarks, setMCQMarks] = useState({
    correct: 0,
    total: 0,
  });

  const createSnackbar = useSnackBar();

  useEffect(() => {
    // Stop trying to fetch exam attempt once backend returns the previous attempt
    // data, or if there is no attempt
    if (isExamAttemptLoaded || isExamAttemptError) setLoadExamAttempt(false);
  }, [isExamAttemptLoaded, isExamAttemptError]);

  useEffect(() => {
    if (examAttempt) {
      // Convert an array of question attempts to an object with questionId as key
      const attemptObject = questionAttemptsArrayToExamAttempt(examAttempt?.data.questions);
      if (attemptObject) setAttempts(attemptObject);
    }
    if (exam) {
      const splitSections = getQuestionSections(exam?.data.questions ?? []);
      setQuestionSections(splitSections);
      setDisplayedQuestions(splitSections[0]?.questions ?? []);
    }
  }, [exam, examAttempt]);

  useEffect(() => {
    if (displayedQuestions[0]?.type === QuestionType.MultipleChoice) {
      setIsMCQ(true);

      // This currently only works if there is one MCQ set, needs updating if not
      setMCQMarks(getExamMCQMarks(displayedQuestions, attempts));

      // Set isMarked to true if all displayed questions have an attempt
      // This is only done for multis at the moment but can be pulled out if needed
      const allMarked = displayedQuestions.every((question: Question) => {
        const attempt = attempts[question.questionId];
        return getIsAttemptFinalised(attempt);
      });
      setIsMarked(allMarked);
    } else setIsMCQ(false);
  }, [displayedQuestions]);

  useEffect(() => {
    setDisplayedQuestions(questionSections[questionSectionIndex]?.questions ?? []);
  }, [questionSections, questionSectionIndex]);

  const changeQuestionSection = (index: number) => {
    setQuestionSectionIndex(index);
  };

  const attemptExamSubmission = async () => {
    const unfinishedQuestions = exam?.data.questions
      .filter((question: Question) => {
        const attempt = attempts[question.questionId];
        return !getIsAttemptFinalised(attempt);
      });

    // Check that every question has been marked so far
    if (unfinishedQuestions?.length && unfinishedQuestions.length > 0) {
      createSnackbar(`Question${unfinishedQuestions.length === 1 ? '' : 's'} ${unfinishedQuestions.map((question: Question) =>
        formatQuestionLabel(question.label)).join(', ')} ${unfinishedQuestions.length === 1 ? 'has' : 'have'} not been marked`, 'error');
    } else {
      setIsSubmitDialogOpen(true);
    }
  };

  const handleProgress = async () => {
    if (questionSectionIndex === questionSections.length - 1) {
      // If currently on the last question, progress button should attempt to submit the exam
      await attemptExamSubmission();
    } else {
      // Otherwise, progress to next question section
      setIsMarked(false);
      changeQuestionSection(questionSectionIndex + 1);
    }
  };

  const handleAttemptChange = async (
    maxMark: number, questionId: string, attempt: AttemptWithMark, unMarkOnChange = true) => {
    if (unMarkOnChange) setIsMarked(false);
    const oldAttempt = attempts[questionId];

    // Update the attept with the new non-undefined values
    const mark = attempt?.mark ?? oldAttempt?.mark;
    const response = attempt?.response ?? oldAttempt?.response;
    const marksBreakdown = attempt?.marksBreakdown;

    // Add maxMark to the attempt
    let newAttempt = {
      ...oldAttempt,
      maxMark,
    };

    // If the new attempt is a MCQ, then add the response
    if (response !== undefined) {
      newAttempt = {
        ...newAttempt,
        response,
      };
    } else {
      // If the new attempt is not a MCQ, then add the mark and marksBreakdown
      newAttempt = {
        ...newAttempt,
        mark,
        marksBreakdown,
      };
    }

    // Don't do anything if the attempt is the same
    if (_.isEqual(oldAttempt, newAttempt)) return;

    // Update the attempt
    setAttempts((prevAttempts) => {
      const newAttempts = {
        ...prevAttempts,
        [questionId]: newAttempt,
      };

      if (isMCQ) setMCQMarks(getExamMCQMarks(displayedQuestions, newAttempts));
      return newAttempts;
    });

    // Put the mark if the mark is finalised
    if (getIsAttemptFinalised(newAttempt)) {
      const result = await putMark({
        subject,
        examId,
        questionId,
        studentId,
        attemptNum,
        attemptData: newAttempt,
      });
      if ('error' in result) {
        setIsUnrecoverableError(true);
      }
    }
  };

  const handleMarking = async () => {
    if (!isMCQ) return;

    // Handle the attempt change for each question in the current section
    setIsMarked(true);
    setIsMarkingLoading(true);

    // eslint-disable-next-line no-restricted-syntax
    for (const question of displayedQuestions) {
      // eslint-disable-next-line no-await-in-loop
      await handleAttemptChange(
        question.maxMark,
        question.questionId,
        {
          response: attempts[question.questionId]?.response ?? 0,
        },
        false,
      );
    }
    setIsMarkingLoading(false);
  };

  /**
   * RENDER
   */

  if (isExamLoading || isStudentLoading || isExamAttemptLoading) {
    return (
      <SkeletonStack />
    );
  }

  if (isExamLoaded && !exam) {
    // If the exam is loaded but no result returned, then block access
    return (
      <NoPermissionMessage />
    );
  }

  return (
    <>
      <Modal
        open={isUnrecoverableError}
        title="Error saving mark"
        titleColor="error"
        description="Please refresh page and try again."
        hideCloseIcon
        buttons={[
          {
            label: 'Refresh',
            onClick: () => window.location.reload(),
            color: 'darkPrimary',
          },
        ]}
      />
      <ResultsDialog
        open={isSubmitDialogOpen}
        onClose={() => setIsSubmitDialogOpen(false)}
        mcqMarks={MCQMarks.correct}
        mcqTotal={MCQMarks.total}
        subject={subject}
        examId={examId}
        studentId={studentId}
        attemptNum={attemptNum}
      />
      <ExamViewer
        heading={`${student?.data.name}'s Exam`}
        subHeading={exam?.data.label}
        questionSections={questionSections}
        questionSectionIndex={questionSectionIndex}
        isSaving={isPutMarkLoading || isMarkingLoading}
        isSavingError={isPutMarkError}
        isSavingSuccess={isPutMarkSuccess}
        isMCQ={isMCQ}
        isMarked={isMarked}
        MCQCorrect={MCQMarks.correct}
        MCQTotal={MCQMarks.total}
        displayedQuestions={displayedQuestions}
        attempts={attempts}
        changeQuestionSection={changeQuestionSection}
        handleMarking={handleMarking}
        handleProgress={handleProgress}
        handleAttemptChange={handleAttemptChange}
      />
    </>
  );
};

export default MarkingPage;
